import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { t } from "@vzlive/shared/i18n";
import { BrandMark } from "../components/BrandMark";
import { LiveChat } from "../components/LiveChat";
import { tenantTheme } from "../lib/branding";
import { useTimeline } from "../hooks/useTimeline";

/**
 * `/embed/chat/:slug` — schmaler Chat-Frame (nur Chat, ohne Feed). Das
 * native Widget <vz-live-ticker> bettet diesen Frame als Sidebar/spalte
 * ein; über ?presence=0 verzichtet der Frame auf eigene Heartbeats, damit
 * Chat-Leser nicht als zweiter Besucher zählen.
 */
export function ChatEmbedPage() {
  const { slug = "" } = useParams();
  const lite =
    new URLSearchParams(window.location.search).get("presence") === "0";
  const { timeline, state, onChatEvent } = useTimeline(slug, {
    presence: !lite,
  });

  useEffect(() => {
    if (!timeline) return;
    const parent = new URLSearchParams(window.location.search).get("parent");
    const targetOrigin =
      parent && /^https?:\/\//.test(parent) ? parent.replace(/\/$/, "") : "*";
    let lastHeight = -1;
    const postHeight = () => {
      const height = document.documentElement.scrollHeight;
      if (!height || height === lastHeight) return;
      lastHeight = height;
      window.parent.postMessage(
        { type: "vzlive:height", height, chat: true },
        targetOrigin,
      );
    };
    const observer = new ResizeObserver(postHeight);
    observer.observe(document.body);
    postHeight();
    // Chatzeilen ans Widget weiterreichen: der Dock-Dock des nativen
    // Embeds zeigt aus ihnen die letzte Leiste als Vorschau.
    const unsubscribe = onChatEvent((change) => {
      if (change.type !== "chat.created") return;
      window.parent.postMessage(
        {
          type: "vzlive:chatline",
          id: change.message.id,
          author: change.message.authorName,
          content: change.message.content,
          replyTo: change.message.replyTo?.authorName ?? "",
        },
        targetOrigin,
      );
    });
    return () => {
      observer.disconnect();
      unsubscribe();
    };
  }, [timeline, onChatEvent]);

  if (!timeline || state === "loading") {
    return (
      <main className="chat-embed">
        <div className="page-loader">
          <BrandMark />
          <span />
        </div>
      </main>
    );
  }

  const { tenant, event } = timeline;
  const locale = tenant.locale;

  if (!tenant.chatEnabled || !event.chatEnabled) {
    return <main className="chat-embed" />;
  }

  return (
    <main
      className="chat-embed"
      style={tenantTheme(tenant.primaryColor, tenant.secondaryColor)}
    >
      <LiveChat
        key={event.id}
        tenant={tenant}
        event={event}
        locale={locale}
        onChatEvent={onChatEvent}
      />
      <p className="sr-only" aria-live="polite">
        {state === "offline" ? t(locale, "offline") : ""}
      </p>
    </main>
  );
}
